import type { SearchSuggestion } from '@recipes/contracts';
import { useQuery } from '@tanstack/react-query';
import { Input, cn } from '@recipes/ui';
import { BookOpen, Carrot, FolderOpen, Search, Tag, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { queryKeys } from '@/lib/query-keys';

export interface RecipeSearchBoxProps {
  value: string;
  /** Called on submit (Enter / suggestion pick). */
  onSearch: (value: string) => void;
  /** Called when a typed suggestion of a specific kind is picked. */
  onPickSuggestion?: (suggestion: SearchSuggestion) => void;
  placeholder?: string;
  className?: string;
}

const ICONS: Record<SearchSuggestion['kind'], typeof Search> = {
  recipe: BookOpen,
  tag: Tag,
  category: FolderOpen,
  ingredient: Carrot,
  collection: FolderOpen,
};

/** Search input with a listbox of server suggestions (combobox pattern). */
export function RecipeSearchBox({
  value,
  onSearch,
  onPickSuggestion,
  placeholder,
  className,
}: RecipeSearchBoxProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounced = useDebouncedValue(draft.trim(), 250);
  const online = useOnlineStatus();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(value), [value]);

  const suggestions = useQuery({
    queryKey: queryKeys.search.suggest(debounced),
    queryFn: () => api.search.suggest(debounced, 8),
    enabled: online && debounced.length >= 2 && open,
    staleTime: 60_000,
    retry: false,
  });
  const items = suggestions.data ?? [];
  const showList = open && items.length > 0;

  const submit = (text: string) => {
    setOpen(false);
    setActive(-1);
    onSearch(text.trim());
  };
  const pick = (s: SearchSuggestion) => {
    setOpen(false);
    setActive(-1);
    if (onPickSuggestion && s.kind !== 'recipe') {
      setDraft(s.kind === 'ingredient' || s.kind === 'tag' ? '' : draft);
      onPickSuggestion(s);
    } else {
      setDraft(s.label);
      onSearch(s.label);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && showList) {
      event.preventDefault();
      setActive((a) => (a + 1) % items.length);
    } else if (event.key === 'ArrowUp' && showList) {
      event.preventDefault();
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (showList && active >= 0) pick(items[active]!);
      else submit(draft);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={t('recipes.search.label')}
        placeholder={placeholder ?? t('recipes.search.placeholder')}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setOpen(true);
          setActive(-1);
          if (e.target.value === '') onSearch('');
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        className="h-11 pl-9 pr-9"
        autoComplete="off"
        enterKeyHint="search"
        data-testid="recipe-search"
      />
      {draft && (
        <button
          type="button"
          className="absolute right-1 top-1/2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
          aria-label={t('recipes.search.clear')}
          onClick={() => {
            setDraft('');
            submit('');
            inputRef.current?.focus();
          }}
        >
          <X className="size-4" aria-hidden />
        </button>
      )}
      <ul
        id={listId}
        role="listbox"
        aria-label={t('recipes.search.suggestions')}
        className={cn(
          'absolute z-40 mt-1 w-full overflow-hidden rounded-md border bg-card shadow-md',
          !showList && 'hidden',
        )}
      >
        {items.map((s, index) => {
          const Icon = ICONS[s.kind];
          return (
            <li
              key={`${s.kind}-${s.id ?? s.label}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={cn(
                'flex min-h-[44px] cursor-pointer items-center gap-2 px-3 py-2 text-sm',
                index === active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent',
              )}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              onMouseEnter={() => setActive(index)}
            >
              <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="flex-1 truncate">{s.label}</span>
              <span className="text-xs text-muted-foreground">
                {s.hint ?? t(`recipes.search.kind.${s.kind}`)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
